import { trimSlashes } from "Utils";
import { getOauthToken, requestRefreshToken } from "./Auth";
import axios from "axios";

const proxyRoot = process.env.REACT_APP_ROOT_API;
const oauthRoot = process.env.REACT_APP_OAUTH_SERVER;

export const buildProxyUrl = (uri, publication) => {
    const baseUrl = proxyRoot.replace("{client_code}", publication.client_code);
    return [trimSlashes(baseUrl), trimSlashes(uri)].join("/");
};

const buildOauthUrl = (uri) => {
    return [trimSlashes(oauthRoot), trimSlashes(uri)].join("/");
};

export async function apiPost(
    uri,
    publication,
    data = {},
    retryOnFail = true,
    tokenAuthenticator = null,
    extraOpts = {}
) {
    const furl = buildProxyUrl(uri, publication);
    let response;
    let tokenCallback = tokenAuthenticator || getOauthToken;

    try {
        response = await axios.post(
            furl,
            data,
            Object.assign(
                {},
                {
                    headers: {
                        Authorization: "Bearer " + tokenCallback(),
                        "X-Publication-Uuid": publication.uuid,
                    },
                },
                extraOpts
            )
        );

        if (response.status === 200) {
            return response.data;
        }
    } catch (e) {
        if (!e.response) {
            throw new Error("Server did not respond.", { cause: e });
        }
        if (e.response.status === 401 && retryOnFail === true) {
            await requestRefreshToken();
            return apiPost(uri, publication, data, false, tokenCallback);
        }
        if (e.response.status !== 200) {
            // TODO error boundary
            throw new Error("Server sent bad status code", { cause: e });
        }

        throw new Error("Unable to refresh authorization.");
    }

    return false;
}

export async function apiPut(uri, publication, data = {}, retryOnFail = true) {
    const furl = buildProxyUrl(uri, publication);
    let response;

    try {
        response = await axios.put(furl, data, {
            headers: {
                Authorization: "Bearer " + getOauthToken(),
                "X-Publication-Uuid": publication.uuid,
            },
        });

        if (response.status === 200) {
            return response.data;
        }
    } catch (e) {
        if (!e.response) {
            throw new Error("Server did not respond.", { cause: e });
        }
        if (e.response.status === 401 && retryOnFail === true) {
            await requestRefreshToken();
            return apiPut(uri, publication, data, false);
        }
        if (e.response.status !== 200) {
            // TODO error boundary
            throw new Error("Server sent bad status code", { cause: e });
        }

        throw new Error("Unable to refresh authorization.");
    }

    return false;
}

export async function apiGet(
    uri,
    publication,
    data = {},
    retryOnFail = true,
    raw = false
) {
    const furl = buildProxyUrl(uri, publication);
    let response;

    try {
        response = await axios.get(furl, {
            headers: {
                Authorization: "Bearer " + getOauthToken(),
                "X-Publication-Uuid": publication.uuid,
            },
            params: data,
        });

        if (response.status === 200) {
            if (raw) {
                return response;
            }
            return response.data;
        }
    } catch (e) {
        if (!e.response) {
            throw new Error("Server did not respond.", { cause: e });
        }
        if (e.response.status === 401 && retryOnFail === true) {
            await requestRefreshToken();
            return apiGet(uri, publication, data, false, raw);
        }
        if (e.response.status !== 200) {
            // TODO error boundary
            throw new Error("Server sent bad status code", { cause: e });
        }
        throw new Error("Unable to refresh authorization.");
    }

    return false;
}

export async function apiDel(uri, publication, retryOnFail = true) {
    const furl = buildProxyUrl(uri, publication);
    let response;

    try {
        response = await axios.delete(furl, {
            headers: {
                Authorization: "Bearer " + getOauthToken(),
                "X-Publication-Uuid": publication.uuid,
            },
        });

        if (response.status === 200) {
            return response.data;
        }
    } catch (e) {
        if (!e.response) {
            throw new Error("Server did not respond.", { cause: e });
        }
        if (e.response.status === 401 && retryOnFail === true) {
            await requestRefreshToken();
            return apiDel(uri, publication, false);
        }
        if (e.response.status !== 200) {
            // TODO error boundary
            throw new Error("Server sent bad status code", { cause: e });
        }
    }
    throw new Error("Unable to refresh authorization.");

    // return false;
}

export async function oauthGet(uri, data = {}, retryOnFail = true) {
    const furl = buildOauthUrl(uri);
    let response;

    try {
        response = await axios.get(furl, {
            headers: {
                Authorization: "Bearer " + getOauthToken(),
            },
            params: data,
        });

        if (response.status === 200) {
            return response.data;
        }
    } catch (e) {
        if (!e.response) {
            throw new Error("Server did not respond.", { cause: e });
        }
        if (e.response.status === 401 && retryOnFail === true) {
            const token = await requestRefreshToken();
            if (!token) {
                throw new Error("Unable to refresh token", { cause: e });
            }
            return oauthGet(uri, data, false);
        }
        if (e.response.status !== 200) {
            // TODO error boundary
            throw new Error("Server sent bad status code", { cause: e });
        }
    }

    return false;
}

export async function oauthDel(uri, retryOnFail = true) {
    const furl = buildOauthUrl(uri);
    let response;

    try {
        response = await axios.delete(furl, {
            headers: {
                Authorization: "Bearer " + getOauthToken(),
            },
        });

        if (response.status === 200) {
            return response.data;
        }
    } catch (e) {
        if (!e.response) {
            throw new Error(e);
        }
        if (e.response.status === 401 && retryOnFail === true) {
            const token = await requestRefreshToken();
            if (!token) {
                throw new Error("Unable to refresh token", { cause: e });
            }
            return oauthDel(uri, false);
        }
        if (e.response.status !== 200) {
            // TODO error boundary
            throw new Error("Server sent bad status code", { cause: e });
        }
    }

    return false;
}

export function messageFromException(e, fallback = null) {
    if (
        e.cause?.response &&
        e.cause.response.data &&
        e.cause.response.data.message
    ) {
        return e.cause.response.data.message;
    }
    if (e.cause?.message) {
        return e.cause.message;
    }

    if (fallback) {
        return fallback;
    }

    return e.message;
}
